/* eslint-disable max-lines */
/* eslint-disable max-statements */
// For a revision code we create a view model for the whole screen
// This also receives all the api model data

import { companies } from 'config/companies';
import { justificationReasons } from 'config/justificationReasons';
import _ from 'lodash';
import { WorkCenter } from 'models/workcenters';
import moment from 'moment';
import {
  Company,
  MODE,
  RevisionCode,
  STATUS,
  Week,
  WorkCenterViewModel,
  WorkCenterWeek
} from '../Incrementals.types';
import { UiCommands } from './UiCommands';

// for building the UI View Model.
export function buildViewModelCommand(
  this: UiCommands,
  revisionCode: RevisionCode,
  workCenters: WorkCenter[]
) {
  // reset view model state to prevent ui state bugs
  this.uiState.currentWeekId = null;
  this.uiState.weeks = [];

  const companiesList = companies;

  this.uiState.justificationReasons = justificationReasons.map(
    (justificationReasonElement) => ({
      id: justificationReasonElement.id,
      description: justificationReasonElement.description
    })
  );

  this.uiState.companies = companiesList.map((companyElement) => ({
    company: companyElement.id,
    description: companyElement.name,
    isActive: companyElement.isActive
  }));

  this.uiState.workCenters = workCenters.map((workCenterElement) => ({
    workCenter: workCenterElement.id,
    description: workCenterElement.description,
    justificationReason: null,
    companies: workCenterElement.companies.map(
      (workCenterCompanyId) =>
        this.uiState.companies.filter(
          (companyElement) => companyElement.company === workCenterCompanyId
        )[0]
    ),
    companiesToAssociate: []
  }));

  this.uiState.currentRevisionCode = revisionCode;

  const company1: Company = {
    company: 'iol',
    description: 'Imperial Oil',
    isActive: true
  };

  const company2: Company = {
    company: 'graham',
    description: 'GRAHAM',
    isActive: true
  };

  const workCenter1: WorkCenterViewModel = {
    workCenter: 'KFRTMILL',
    description: 'KFRTMILL Description',
    companiesToAssociate: [],
    companies: [company1, company2]
  };

  const workCenterWeek1: WorkCenterWeek = {
    workCenter: workCenter1,
    justificationReason: null,
    mode: MODE.NONE,
    status: STATUS.REQUESTED,
    days: [
      {
        date: moment('2023-01-02'),
        baseline: { day: 1, night: 2 },
        required: { day: 2, night: 4 },
        incrementals: [
          {
            approved: { day: 5, night: 6 },
            company: company1,
            requested: { day: 7, night: 8 },
            userValue: { day: '9', night: '10' }
          },
          {
            approved: { day: 5, night: 6 },
            company: company2,
            requested: { day: 7, night: 8 },
            userValue: { day: '9', night: '10' }
          }
        ]
      },
      {
        date: moment('2023-01-03'),
        baseline: { day: 1, night: 2 },
        required: { day: 3, night: 4 },
        incrementals: [
          {
            approved: { day: 5, night: 6 },
            company: company1,
            requested: null,
            userValue: null
          },
          {
            approved: { day: 5, night: 6 },
            company: company2,
            requested: null,
            userValue: null
          }
        ]
      },
      {
        date: moment('2023-01-04'),
        baseline: { day: 1, night: 2 },
        required: { day: 3, night: 4 },
        incrementals: [
          {
            approved: null,
            company: company1,
            requested: null,
            userValue: null
          },
          {
            approved: null,
            company: company2,
            requested: null,
            userValue: null
          }
        ]
      },
      {
        date: moment('2023-01-05'),
        baseline: { day: 1, night: 2 },
        required: { day: 3, night: 4 },
        incrementals: [
          {
            approved: null,
            company: company1,
            requested: { day: 7, night: 8 },
            userValue: null
          },
          {
            approved: null,
            company: company2,
            requested: { day: 7, night: 8 },
            userValue: null
          }
        ]
      },
      {
        date: moment('2023-01-06'),
        baseline: { day: 1, night: 2 },
        required: { day: 3, night: 4 },
        incrementals: [
          {
            approved: null,
            company: company1,
            requested: null,
            userValue: { day: '9', night: '10' }
          },
          {
            approved: null,
            company: company2,
            requested: null,
            userValue: { day: '9', night: '10' }
          }
        ]
      },
      {
        date: moment('2023-01-07'),
        baseline: { day: 1, night: 2 },
        required: { day: 3, night: 4 },
        incrementals: [
          {
            approved: { day: 5, night: 6 },
            company: company1,
            requested: { day: 7, night: 8 },
            userValue: null
          },
          {
            approved: { day: 5, night: 6 },
            company: company2,
            requested: { day: 7, night: 8 },
            userValue: null
          }
        ]
      },
      {
        date: moment('2023-01-08'),
        baseline: { day: 1, night: 2 },
        required: { day: 3, night: 4 },
        incrementals: [
          {
            approved: { day: 5, night: 6 },
            company: company1,
            requested: { day: 7, night: 8 },
            userValue: null
          },
          {
            approved: { day: 5, night: 6 },
            company: company2,
            requested: { day: 7, night: 8 },
            userValue: null
          }
        ]
      }
    ]
  };

  const workCenterWeek2: WorkCenterWeek = {
    workCenter: workCenter1,
    justificationReason: null,
    mode: MODE.NONE,
    status: STATUS.APPROVED,
    days: [
      {
        date: moment('2023-01-09'),
        baseline: { day: 1, night: 2 },
        required: { day: 3, night: 4 },
        incrementals: [
          {
            approved: { day: 5, night: 6 },
            company: company1,
            requested: { day: 7, night: 8 },
            userValue: { day: '9', night: '10' }
          }
        ]
      },
      {
        date: moment('2023-01-10'),
        baseline: { day: 1, night: 2 },
        required: { day: 3, night: 4 },
        incrementals: [
          {
            approved: { day: 5, night: 6 },
            company: company1,
            requested: null,
            userValue: null
          }
        ]
      },
      {
        date: moment('2023-01-11'),
        baseline: { day: 1, night: 2 },
        required: { day: 3, night: 4 },
        incrementals: [
          {
            approved: null,
            company: company1,
            requested: null,
            userValue: null
          }
        ]
      },
      {
        date: moment('2023-01-12'),
        baseline: { day: 1, night: 2 },
        required: { day: 3, night: 4 },
        incrementals: [
          {
            approved: null,
            company: company1,
            requested: { day: 7, night: 8 },
            userValue: null
          }
        ]
      },
      {
        date: moment('2023-01-13'),
        baseline: { day: 1, night: 2 },
        required: { day: 3, night: 4 },
        incrementals: [
          {
            approved: null,
            company: company1,
            requested: null,
            userValue: { day: '9', night: '10' }
          }
        ]
      },
      {
        date: moment('2023-01-14'),
        baseline: { day: 1, night: 2 },
        required: { day: 3, night: 4 },
        incrementals: [
          {
            approved: null,
            company: company1,
            requested: null,
            userValue: { day: '9', night: '10' }
          }
        ]
      }
    ]
  };

  const workCenterWeek3: WorkCenterWeek = {
    workCenter: workCenter1,
    justificationReason: null,
    mode: MODE.NONE,
    status: STATUS.APPROVED,
    days: [
      {
        date: moment('2023-01-18'),
        baseline: { day: 1, night: 2 },
        required: { day: 3, night: 4 },
        incrementals: [
          {
            approved: null,
            company: company1,
            requested: null,
            userValue: { day: '9', night: '10' }
          }
        ]
      }
    ]
  };

  const week1: Week = {
    weekId: {
      startOfWeek: moment('2023-01-02'),
      endOfWeek: moment('2023-01-08')
    },
    workCenterWeeks: [workCenterWeek1]
  };

  week1.workCenterWeeks = _.range(160).map((i) => {
    const workCenterWeek = _.cloneDeep(workCenterWeek1);
    workCenterWeek.workCenter.workCenter += `${i}`;
    return workCenterWeek;
  });

  const week2: Week = {
    weekId: {
      startOfWeek: moment('2023-01-09'),
      endOfWeek: moment('2023-01-15')
    },
    workCenterWeeks: [workCenterWeek2]
  };

  const week3: Week = {
    weekId: {
      startOfWeek: moment('2023-01-18'),
      endOfWeek: moment('2023-01-24')
    },
    workCenterWeeks: [workCenterWeek3]
  };

  if (revisionCode.code === 'RevCode1') {
    this.uiState.weeks = [week1, week2];
  } else {
    this.uiState.weeks = [week3];
  }

  // eslint-disable-next-line no-use-before-define
  this.uiState.currentWeekId = this.uiQueries.getFirstActiveWeek().weekId;
  this.setUiStateTriggerReRender(this.uiState);
}
